import { useMemo, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { PROPERTY_TYPE_OPTIONS } from "../neighborhoodSearch/constants";

export interface PropertyTypeOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

export default function PropertyTypeFilter({
  selectedPropertyTypes,
  onPropertyTypesChange,
  options = PROPERTY_TYPE_OPTIONS,
}: {
  selectedPropertyTypes: string[];
  onPropertyTypesChange?: (types: string[]) => void;
  options?: readonly PropertyTypeOption[];
}) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const togglePropertyType = (type: string) => {
    if (selectedPropertyTypes.includes(type)) {
      onPropertyTypesChange?.(selectedPropertyTypes.filter((t) => t !== type));
    } else {
      onPropertyTypesChange?.(selectedPropertyTypes.concat(type));
    }
  };

    // Use options prop for allPropertyTypes
    const allPropertyTypes = useMemo(() => {
      // Copy the options and insert the expander after the 5th item
      const baseOptions = options.slice(0, 5);
      const expander = {
        value: "expander",
        icon: isExpanded ? (
          <ChevronUp className="h-6 w-6" />
        ) : (
          <ChevronDown className="h-6 w-6" />
        ),
        label: isExpanded ? "Less" : "More",
      };
      const restOptions = options.slice(5);
      return [...baseOptions, expander, ...restOptions];
    }, [isExpanded, options]);

  // Filter property types based on isExpanded state
  const propertyTypesListOptions = useMemo(() => {
      // Always show the first 6 (5 + expander)
    const baseTypes = allPropertyTypes.slice(0, 6);
    // Only show the additional types when expanded
    const additionalTypes = isExpanded ? allPropertyTypes.slice(6) : [];
    return [...baseTypes, ...additionalTypes];
  }, [allPropertyTypes, isExpanded]);

  return (
    <div className="w-full flex justify-center">
      <div className="grid grid-cols-3 gap-1 justify-items-center">
          {propertyTypesListOptions.map(({ value, label, icon }) => {
          if (value === "expander") {
            return (
              <button
                key={value}
                className={`flex flex-col items-center justify-center p-4 rounded-lg border border-gray-300 h-[96px] w-[96px] ${
                  selectedPropertyTypes.includes(value)
                    ? "bg-primary-light border-orange-500"
                    : "bg-white border-gray-300"
                }`}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <div
                  className={`${
                    selectedPropertyTypes.includes(value)
                      ? "text-orange-500"
                      : "text-gray-600"
                  }`}
                >
                    {isExpanded ? (
                      <ChevronUp className="h-6 w-6" />
                    ) : (
                      <ChevronDown className="h-6 w-6" />
                    )}
                </div>
                <div
                  className={`text-sm font-medium ${
                    selectedPropertyTypes.includes(value)
                      ? "text-orange-500"
                      : "text-gray-600"
                  }`}
                >
                  {label}
                </div>
              </button>
            );
          }
          return (
            <button
              key={value}
              className={`flex flex-col items-center justify-center p-4 rounded-lg border h-[96px] w-[96px] ${
                selectedPropertyTypes.includes(value)
                  ? "bg-primary-light border-orange-500"
                  : "bg-white border-gray-300"
              }`}
              onClick={() => togglePropertyType(value)}
            >
              <div
                className={`${
                  selectedPropertyTypes.includes(value)
                    ? "text-orange-500"
                    : "text-gray-600"
                }`}
              >
                  {icon}
              </div>
              <div
                className={`text-sm font-medium ${
                  selectedPropertyTypes.includes(value)
                    ? "text-orange-500"
                    : "text-gray-600"
                }`}
              >
                {label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
